import { motion } from "@/lib/motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { skipIntro } from "@/lib/prerender";

const ease = [0.22, 1, 0.36, 1];

// Mount-triggered reveal (fade + rise on mount) — the proven pattern from the
// Enterprise/Healthcare pages that keeps every section visible (opacity:1) in
// normal document flow regardless of scroll position or headless capture.
export const MountReveal = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={skipIntro() ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay, ease }}
        className={className}
    >
        {children}
    </motion.div>
);

// Shared CTA link matching the established button styling. Internal URLs (start
// with "/") use react-router navigation; external URLs open in a new tab with
// safe rel attributes when openInNewTab is true.
export const CtaLink = ({
    href = "#",
    label,
    openInNewTab = false,
    variant = "primary",
    testid,
    className = "",
}) => {
    const base =
        "group inline-flex items-center gap-2 rounded-control px-7 py-3.5 text-sm font-semibold transition-colors duration-150";
    const styles =
        variant === "primary"
            ? "bg-brand-accent text-brand-on-accent"
            : "border-[1.5px] border-brand-control-border text-brand-text hover:bg-brand-surface2";
    const classes = `${base} ${styles} ${className}`;
    const icon = (
        <ArrowUpRight className="h-4 w-4 transition-colors duration-150" />
    );

    const isInternal = href.startsWith("/") && !openInNewTab;
    if (isInternal) {
        return (
            <Link to={href} data-testid={testid} className={classes}>
                {label}
                {icon}
            </Link>
        );
    }
    const ext = openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {};
    return (
        <a href={href} {...ext} data-testid={testid} className={classes}>
            {label}
            {icon}
        </a>
    );
};
